import {
  MSI_GEOMETRY,
  MSI_TYPE_SCALE,
} from './msiStyleTokens';
import { getThemeAppStyleTokens } from './themeTokens';

export const appBarActionButtonSx = {
  width: MSI_GEOMETRY.controlHeight,
  height: MSI_GEOMETRY.controlHeight,
  minWidth: MSI_GEOMETRY.controlHeight,
  flexShrink: 0,
  p: 0,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: `${MSI_GEOMETRY.radii.button}px`,
  backgroundColor: 'background.paper',
  color: 'text.secondary',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: 'action.hover',
    borderColor: 'divider',
    color: 'text.primary',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
  '&.Mui-disabled': { borderColor: 'divider' },
};

export const appBarDividerSx = {
  height: 32,
  alignSelf: 'center',
  flexShrink: 0,
  mx: 1,
  borderColor: 'divider',
};

export function buildMsiAppBarStyles(themeId) {
  const {
    palette: MSI_PALETTE,
    componentColors: MSI_COMPONENT_COLORS,
    chrome,
  } = getThemeAppStyleTokens(themeId);

  const chromeControl = {
    borderColor: chrome.divider,
    borderRadius: `${MSI_GEOMETRY.radii.iconButton}px`,
    backgroundColor: chrome.controlBackground,
    color: chrome.secondaryText,
    boxShadow: 'none',
    '&:hover': {
      borderColor: chrome.activeBorder,
      backgroundColor: chrome.hover,
      color: chrome.primaryText,
    },
    '&:focus-visible': {
      outline: `2px solid ${MSI_COMPONENT_COLORS.focusBorder}`,
      outlineOffset: 2,
    },
  };

  return {
    appBar: {
      border: 0,
      backgroundColor: 'transparent',
      backgroundImage: 'none',
      boxShadow: 'none',
    },
    sidebar: {
      borderColor: chrome.divider,
      background: chrome.background,
      color: chrome.primaryText,
      boxShadow: 'none',
    },
    sidebarDivider: {
      borderColor: chrome.divider,
    },
    toolbar: {
      borderColor: chrome.divider,
      borderRadius: 0,
      background: chrome.background,
      backgroundImage: 'none',
      color: chrome.primaryText,
      backdropFilter: 'none',
      boxShadow: 'none',
    },
    wordmark: {
      color: chrome.primaryText,
      fontSize: MSI_TYPE_SCALE.navigationLabel.fontSize,
      fontWeight: 800,
      letterSpacing: 0,
    },
    environmentBadge: {
      borderColor: MSI_COMPONENT_COLORS.semanticBorders.warning,
      backgroundColor: MSI_PALETTE.warningBg,
      color: MSI_PALETTE.warning,
      letterSpacing: 0,
    },
    utilityControl: chromeControl,
    profileControl: {
      ...chromeControl,
      borderRadius: `${MSI_GEOMETRY.radii.button}px`,
      color: chrome.secondaryText,
      letterSpacing: 0,
    },
    adminBadge: {
      borderColor: MSI_COMPONENT_COLORS.semanticBorders.warning,
      borderRadius: `${MSI_GEOMETRY.radii.statusBadge}px`,
      backgroundColor: MSI_PALETTE.warningBg,
      color: MSI_PALETTE.warning,
      boxShadow: 'none',
    },
    navigationItem: (active) => ({
      minHeight: MSI_GEOMETRY.compactControlHeight,
      borderRadius: `${MSI_GEOMETRY.radii.navigation}px`,
      borderColor: active ? MSI_PALETTE.navActiveBorder : 'transparent',
      backgroundColor: active ? MSI_PALETTE.navActiveFill : 'transparent',
      color: active ? chrome.primaryText : chrome.secondaryText,
      fontSize: MSI_TYPE_SCALE.navigationLabel.fontSize,
      fontWeight: MSI_TYPE_SCALE.navigationLabel.fontWeight,
      letterSpacing: 0,
      textTransform: 'none',
      '&:hover': {
        borderColor: active ? MSI_PALETTE.navActiveBorder : 'transparent',
        backgroundColor: active ? MSI_PALETTE.navActiveFill : MSI_PALETTE.navHover,
        color: chrome.primaryText,
      },
      '&:focus-visible': {
        outline: `2px solid ${MSI_COMPONENT_COLORS.focusBorder}`,
        outlineOffset: 2,
      },
    }),
    dialogPaper: {
      borderColor: MSI_PALETTE.line,
      borderRadius: `${MSI_GEOMETRY.radii.panel}px`,
      backgroundColor: MSI_PALETTE.surface,
      backgroundImage: 'none',
      boxShadow: '0 12px 32px rgba(12, 23, 38, 0.12)',
    },
    menuPaper: {
      borderColor: MSI_PALETTE.line,
      borderRadius: `${MSI_GEOMETRY.radii.panel}px`,
      backgroundColor: MSI_PALETTE.surface,
      backgroundImage: 'none',
      boxShadow: '0 8px 24px rgba(12, 23, 38, 0.12)',
    },
    settingsWorkspacePaper: {
      backgroundColor: MSI_PALETTE.canvas,
      backgroundImage: 'none',
    },
    settingsHeader: {
      borderColor: chrome.divider,
      backgroundColor: chrome.background,
      color: chrome.primaryText,
      backdropFilter: 'none',
      boxShadow: 'none',
    },
    settingsNavigation: {
      borderColor: chrome.divider,
      backgroundColor: chrome.background,
      color: chrome.primaryText,
    },
    settingsSectionCard: {
      borderColor: MSI_PALETTE.line,
      borderRadius: `${MSI_GEOMETRY.radii.panel}px`,
      backgroundColor: MSI_PALETTE.surface,
      backgroundImage: 'none',
      boxShadow: 'none',
    },
    settingsMutedSurface: MSI_PALETTE.surfaceSubtle,
    settingsPanelSurface: MSI_PALETTE.surface,
    settingsTabs: {
      '& .MuiTab-root': {
        borderColor: 'transparent',
        borderRadius: `${MSI_GEOMETRY.radii.navigation}px`,
        backgroundColor: 'transparent',
        color: chrome.secondaryText,
        letterSpacing: 0,
        boxShadow: 'none',
        '&:hover': {
          borderColor: 'transparent',
          backgroundColor: chrome.hover,
          color: chrome.primaryText,
        },
      },
      '& .Mui-selected': {
        borderColor: chrome.activeBorder,
        backgroundColor: chrome.activeFill,
        color: chrome.primaryText,
        boxShadow: 'none',
      },
    },
    settingsChoice: (selected) => ({
      borderColor: selected ? MSI_COMPONENT_COLORS.focusBorder : MSI_PALETTE.line,
      borderRadius: `${MSI_GEOMETRY.radii.button}px`,
      backgroundColor: selected ? MSI_PALETTE.blue100 : MSI_PALETTE.surfaceSubtle,
      color: MSI_PALETTE.ink900,
      boxShadow: 'none',
      '&:hover': {
        borderColor: selected ? MSI_COMPONENT_COLORS.focusBorder : MSI_PALETTE.lineDark,
        backgroundColor: selected ? MSI_PALETTE.blue100 : MSI_COMPONENT_COLORS.rowHover,
        boxShadow: 'none',
      },
    }),
  };
}
